import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAtom } from 'jotai';
import { ActionIcon, Card, Group, Stack, Text, Textarea, Modal, TextInput, Button, Select, Switch, Divider, Popover } from '@mantine/core';
import { IconArrowLeft, IconMessageDots, IconPlus, IconMoodSmile, IconPhoto, IconCamera, IconFile, IconMapPin, IconUser, IconMicrophone, IconMusic, IconSticker2, IconSettings, IconHeart, IconHeartFilled, IconRepeat, IconTrash, IconSend, } from '@tabler/icons-react';
import { activePageAtom } from '@/stores/atoms/uiAtoms';
import { useSlideExit } from '@/renderer/utils/pageTransitions';
import { notifications } from '@mantine/notifications';
import { chatUiStyleAtom, chatPreferencesAtom, chatAvatarDataUrlAtom, chatNicknameAtom, chatStatusAtom, chatModelUrlAtom, chatModelNameAtom, chatModelApiKeyAtom, waPersonasAtom, waMessagesMapAtom, waCurrentPersonaIdAtom, waInitDoneAtom, waSelfNicknameAtom, waSelfDefineAtom, waSelfMemoryAtom, waMaxMsgCntAtom, waWallpaperMapAtom, waFeedsAtom } from '@/stores/atoms/whatsappAtoms';
import { httpFetch } from '@/utils/http';
import { getApiBase, resolveServerUrl, getOrCreateUserId } from '@/utils/session';
import { authUserAtom, authReadyAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { getStoredAuthToken } from '@/utils/userApi';
import { fetchModels } from '@/renderer/utils/gameApi';
import { chatAsync } from '@/utils/chatAsyncWs';
import { confirmDialog } from '@/utils/confirm';
const defaultPersonas = [
    { id: 'p_default', name: '默认', define: '一个友善的聊天对象，语气轻松、礼貌。', avatar: 'https://picsum.photos/seed/default-wa/64/64', last: '欢迎开始聊天', time: '刚刚' },
];
const WhatsApp = () => {
    const [, setActivePage] = useAtom(activePageAtom);
    const [tab, setTab] = React.useState('messages');
    const [personas, setPersonas] = useAtom(waPersonasAtom);
    const [currentId, setCurrentId] = useAtom(waCurrentPersonaIdAtom);
    const [messagesMap, setMessagesMap] = useAtom(waMessagesMapAtom);
    const [feeds, setFeeds] = useAtom(waFeedsAtom);
    const [initDone, setInitDone] = useAtom(waInitDoneAtom);
    const [authUser] = useAtom(authUserAtom);
    const [authReady] = useAtom(authReadyAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    const current = React.useMemo(() => personas.find((p) => p.id === currentId) || null, [personas, currentId]);
    const messages = React.useMemo(() => (current ? (messagesMap[current.id] || []) : []), [messagesMap, current?.id]);
    const [draft, setDraft] = React.useState('');
    const { startExit, containerStyle, underlayStyle, panelStyle } = useSlideExit();
    const [showActions, setShowActions] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [newName, setNewName] = React.useState('');
    const [newDefine, setNewDefine] = React.useState('');
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    const [subView, setSubView] = React.useState('conversation');
    const [nickModalOpen, setNickModalOpen] = React.useState(false);
    const [nickDraft, setNickDraft] = React.useState('');
    const wallpaperFileRef = React.useRef(null);
    const [commentModalOpen, setCommentModalOpen] = React.useState(false);
    const [commentDraft, setCommentDraft] = React.useState('');
    const [commentTargetId, setCommentTargetId] = React.useState(null);
    const [typing, setTyping] = React.useState(false);
    const [emojiOpen, setEmojiOpen] = React.useState(false);
    const textareaId = 'wa-draft-input';
    const [wsStatus, setWsStatus] = React.useState({ state: 'open', attempts: 0 });
    const insertEmoji = React.useCallback((emoji) => {
        const el = (typeof document !== 'undefined') ? document.getElementById(textareaId) : null;
        const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : draft.length;
        const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : draft.length;
        const before = draft.slice(0, start);
        const after = draft.slice(end);
        const next = `${before}${emoji}${after}`;
        setDraft(next);
        setEmojiOpen(false);
        setTimeout(() => {
            try {
                el?.focus();
                const pos = start + emoji.length;
                el?.setSelectionRange(pos, pos);
            }
            catch { }
        }, 0);
    }, [draft]);
    const EMOJIS = React.useMemo(() => (['😀', '😃', '😄', '😁', '😆', '😊', '🙂', '😉', '🥰', '😍', '😘', '😚', '😙', '😋', '😜', '🤪', '🤗', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😴', '😪', '😌', '😮', '😲', '🤯', '😳', '🥺', '😭', '😤', '😡', '👍', '👎', '🙏', '👏', '💪', '🔥', '✨', '💖', '🎉', '🫶', '🤝', '🤞', '👌', '🤌']), []);
    // Summary states
    const [summaryLoading, setSummaryLoading] = React.useState(false);
    const [summaryModalOpen, setSummaryModalOpen] = React.useState(false);
    const [summaryDraft, setSummaryDraft] = React.useState('');
    const summaryMetaRef = React.useRef({});
    // Scroll + new-message indicator
    const msgsScrollRef = React.useRef(null);
    const [atBottom, setAtBottom] = React.useState(true);
    const atBottomRef = React.useRef(true);
    const [hasNew, setHasNew] = React.useState(false);
    const lastCountRef = React.useRef(0);
    // Long-press message actions
    const [msgActionsOpen, setMsgActionsOpen] = React.useState(false);
    const [msgActionsIndex, setMsgActionsIndex] = React.useState(null);
    const [msgActionsRole, setMsgActionsRole] = React.useState(null);
    const lpTimerRef = React.useRef(null);
    const [editingRange, setEditingRange] = React.useState(null);
    const [editingDraftMsg, setEditingDraftMsg] = React.useState('');
    // Unified modal template styles for WhatsApp
    const modalCommonProps = {
        withCloseButton: false,
        radius: 'md',
        shadow: 'md',
        styles: {
            header: { justifyContent: 'center', backgroundColor: 'var(--app-bg-secondary)' },
            title: { width: '100%' },
            body: { backgroundColor: 'var(--app-bg-secondary)' },
            content: { backgroundColor: 'var(--app-bg-secondary)' },
        },
    };
    // Chat personalization atoms
    const [chatStyle, setChatStyle] = useAtom(chatUiStyleAtom);
    const [wallpapers, setWallpapers] = useAtom(waWallpaperMapAtom);
    const [prefs, setPrefs] = useAtom(chatPreferencesAtom);
    const [avatar, setAvatar] = useAtom(chatAvatarDataUrlAtom);
    const [nickname, setNickname] = useAtom(chatNicknameAtom);
    const [status, setStatus] = useAtom(chatStatusAtom);
    const [modelUrl, setModelUrl] = useAtom(chatModelUrlAtom);
    const [modelName, setModelName] = useAtom(chatModelNameAtom);
    const [apiKey, setApiKey] = useAtom(chatModelApiKeyAtom);
    // 自我人设
    const [selfNickname, setSelfNickname] = useAtom(waSelfNicknameAtom);
    const [selfDefine, setSelfDefine] = useAtom(waSelfDefineAtom);
    const [selfMemory, setSelfMemory] = useAtom(waSelfMemoryAtom);
    const [maxMsgCnt, setMaxMsgCnt] = useAtom(waMaxMsgCntAtom);
    const [maxCntDraft, setMaxCntDraft] = React.useState('');
    const [models, setModels] = React.useState([]);
    // Keep "我的昵称" default in sync with “我”页昵称：当 selfNickname 为空时，取我页昵称或 'user'
    React.useEffect(() => {
        const base = (String(nickname || '').trim()) || 'user';
        if (!selfNickname || !String(selfNickname).trim()) {
            try {
                setSelfNickname(base);
            }
            catch { }
        }
    }, [nickname]);
    // Debounced server send after user activity to simulate human behavior
    const pendingTimerRef = React.useRef(null);
    const pendingPersonaRef = React.useRef(null);
    const latestMessagesRef = React.useRef([]);
    const latestPersonaIdRef = React.useRef(null);
    React.useEffect(() => {
        latestMessagesRef.current = messages || [];
        latestPersonaIdRef.current = current?.id || null;
    }, [messages, current?.id]);
    const restartPendingTimer = React.useCallback(() => {
        if (pendingTimerRef.current) {
            try {
                window.clearTimeout(pendingTimerRef.current);
            }
            catch { }
            pendingTimerRef.current = null;
        }
        if (!current?.id)
            return;
        pendingPersonaRef.current = current.id;
        pendingTimerRef.current = window.setTimeout(async () => {
            pendingTimerRef.current = null;
            // Persona switched while waiting; ignore
            if (!pendingPersonaRef.current || pendingPersonaRef.current !== latestPersonaIdRef.current)
                return;
            try {
                // Build compact history at send time to include all inputs so far
                const personaId = latestPersonaIdRef.current || current.id;
                const list = (latestMessagesRef.current || []);
                const historyForServer = buildCompactHistory(list);
                const displayName = (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || 'user';
                const uid = getOrCreateUserId();
                const now = new Date();
                const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                const payload = {
                    model: (modelName && modelName.trim()) || 'qwen3-7b',
                    persona_id: personaId,
                    history: historyForServer,
                    globals: {
                        user: displayName,
                        user_title: displayName,
                        char: (current?.name ?? ''),
                        persona: (current?.define ?? ''),
                        persona_description: (() => { const a = String(selfDefine || '').trim(); const b = String(selfMemory || '').trim(); return [a, b].filter(Boolean).join('\n'); })(),
                        max_msg_cnt: (() => { const n = Number(maxMsgCnt); return Number.isFinite(n) && n > 0 ? Math.floor(n) : 7; })(),
                        date: dateStr,
                        time: timeStr,
                    },
                    user_id: uid,
                };
                const token = getStoredAuthToken();
                if (!token) {
                    setAuthModalOpen(true);
                    return;
                }
                setTyping(true);
                await chatAsync.sendRequestLazy(payload);
            }
            catch (e) {
                setTyping(false);
            }
        }, 3000);
    }, [current?.id, messagesMap, selfNickname, nickname, modelName, selfDefine, selfMemory, maxMsgCnt, setAuthModalOpen]);
    const cancelPendingTimer = React.useCallback(() => {
        if (pendingTimerRef.current) {
            try {
                window.clearTimeout(pendingTimerRef.current);
            }
            catch { }
            pendingTimerRef.current = null;
        }
        pendingPersonaRef.current = null;
    }, []);
    // First run: fetch initial personas; otherwise seed default when init is done but empty
    React.useEffect(() => {
        (async () => {
            try {
                if (!initDone && (!personas || personas.length === 0)) {
                    // Require login: if not authenticated, open login modal and skip init
                    const token = getStoredAuthToken();
                    if (!authReady || !authUser || !token) {
                        try {
                            setAuthModalOpen(true);
                        }
                        catch { }
                        return;
                    }
                    const base = getApiBase();
                    const res = await httpFetch(`${base}/api/personas/init`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const list = Array.isArray(data?.personas) ? data.personas : [];
                        const mapped = list.map((p) => ({
                            id: String(p?.id ?? `p_${Math.random().toString(36).slice(2)}`),
                            name: String(p?.name ?? '角色'),
                            define: String(p?.description ?? ''),
                            avatar: resolveServerUrl(p?.avatar_url || '') || 'https://picsum.photos/seed/wa-default/64/64',
                        }));
                        if (mapped.length > 0) {
                            setPersonas(mapped);
                            setInitDone(true);
                            return;
                        }
                    }
                }
                if (initDone && (!personas || personas.length === 0)) {
                    setPersonas(defaultPersonas);
                }
            }
            catch {
                // ignore; if init not done, next启动会再尝试
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initDone, authReady, authUser]);
    // Fetch models list once and default to first if unset or invalid (by model code)
    React.useEffect(() => {
        (async () => {
            try {
                const uid = getOrCreateUserId();
                const list = await fetchModels(uid, 'zh-CN');
                setModels(list || []);
                if (Array.isArray(list) && list.length) {
                    const codes = new Set((list || []).map((m) => (m?.model || '').trim()).filter(Boolean));
                    const cur = (modelName || '').trim();
                    if (!cur || !codes.has(cur)) {
                        const first = (list[0]?.model || '').trim();
                        if (first)
                            setModelName(first);
                    }
                }
            }
            catch { }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const appendMessageForPersona = React.useCallback((personaId, role, text) => {
        const msg = { id: String(Date.now()), role, text, ts: Date.now() };
        setMessagesMap((prev) => {
            const list = prev?.[personaId] || [];
            return { ...prev, [personaId]: [...list, msg] };
        });
    }, [setMessagesMap]);
    const appendMessage = React.useCallback((role, text) => {
        if (!current)
            return;
        appendMessageForPersona(current.id, role, text);
    }, [current, appendMessageForPersona]);
    // Long press helpers
    const lpStart = (idx, role) => {
        try {
            if (lpTimerRef.current)
                window.clearTimeout(lpTimerRef.current);
        }
        catch { }
        lpTimerRef.current = window.setTimeout(() => {
            setMsgActionsIndex(idx);
            setMsgActionsRole(role);
            setMsgActionsOpen(true);
        }, 500);
    };
    const lpCancel = () => {
        if (lpTimerRef.current) {
            try {
                window.clearTimeout(lpTimerRef.current);
            }
            catch { }
            lpTimerRef.current = null;
        }
    };
    const beginEdit = (idx) => {
        if (!current)
            return;
        const list = (messagesMap[current.id] || []);
        const m = list[idx];
        if (!m)
            return;
        if (m.role === 'them') {
            // group contiguous 'them' messages as one server turn
            let start = idx, end = idx;
            while (start - 1 >= 0 && list[start - 1].role === 'them')
                start--;
            while (end + 1 < list.length && list[end + 1].role === 'them')
                end++;
            const combined = list.slice(start, end + 1).map(x => x.text).join('\n');
            setEditingRange({ start, end, role: 'them' });
            setEditingDraftMsg(combined);
        }
        else {
            setEditingRange({ start: idx, end: idx, role: 'me' });
            setEditingDraftMsg(m.text);
        }
        setMsgActionsOpen(false);
    };
    const saveEdit = () => {
        if (!current || !editingRange) {
            setEditingRange(null);
            return;
        }
        const list = (messagesMap[current.id] || []);
        const { start, end, role } = editingRange;
        if (start < 0 || end >= list.length || start > end) {
            setEditingRange(null);
            return;
        }
        const next = list.slice();
        if (role === 'them') {
            // replace the whole group with a single combined message
            const head = next.slice(0, start);
            const tail = next.slice(end + 1);
            head.push({ id: `m${Date.now()}`, role: 'them', text: editingDraftMsg, ts: Date.now() });
            setMessagesMap({ ...messagesMap, [current.id]: head.concat(tail) });
        }
        else {
            next[start] = { ...next[start], text: editingDraftMsg, ts: Date.now() };
            setMessagesMap({ ...messagesMap, [current.id]: next });
        }
        setEditingRange(null);
        setEditingDraftMsg('');
    };
    const cancelEdit = () => { setEditingRange(null); setEditingDraftMsg(''); };
    // Helpers: scroll handling
    const computeAtBottom = React.useCallback(() => {
        const el = msgsScrollRef.current;
        if (!el)
            return true;
        const threshold = 24;
        return (el.scrollTop + el.clientHeight) >= (el.scrollHeight - threshold);
    }, []);
    const scrollToBottom = React.useCallback((smooth = true) => {
        const el = msgsScrollRef.current;
        if (!el)
            return;
        try {
            el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        }
        catch {
            el.scrollTop = el.scrollHeight;
        }
    }, []);
    const onScrollMsgs = React.useCallback(() => {
        const at = computeAtBottom();
        setAtBottom(at);
        atBottomRef.current = at;
        if (at && hasNew)
            setHasNew(false);
    }, [computeAtBottom, hasNew]);
    // When new server messages arrive, auto scroll if already at bottom; else show indicator
    React.useEffect(() => {
        const prev = lastCountRef.current;
        const cur = (messages || []).length;
        if (cur > prev) {
            const last = (messages || [])[cur - 1];
            if (last && last.role === 'them') {
                if (atBottomRef.current) {
                    // Next tick to ensure DOM rendered
                    requestAnimationFrame(() => scrollToBottom(true));
                }
                else {
                    setHasNew(true);
                }
            }
            else {
                // For user-sent messages, prefer to keep current scroll state
            }
        }
        lastCountRef.current = cur;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages?.length]);
    const deleteAt = async (idx) => {
        if (!current)
            return;
        const ok = await confirmDialog({ title: '确认', message: '确定删除该条消息？', labels: { confirm: '确定', cancel: '取消' } });
        if (!ok)
            return;
        const list = (messagesMap[current.id] || []);
        if (idx < 0 || idx >= list.length)
            return;
        let start = idx, end = idx;
        if (list[idx].role === 'them') {
            while (start - 1 >= 0 && list[start - 1].role === 'them')
                start--;
            while (end + 1 < list.length && list[end + 1].role === 'them')
                end++;
        }
        const next = list.slice(0, start).concat(list.slice(end + 1));
        setMessagesMap({ ...messagesMap, [current.id]: next });
        setMsgActionsOpen(false);
    };
    const regenerateAt = async (idx) => {
        if (!current)
            return;
        const list = (messagesMap[current.id] || []);
        if (!list[idx] || list[idx].role !== 'them') {
            setMsgActionsOpen(false);
            return;
        }
        let start = idx, end = idx;
        while (start - 1 >= 0 && list[start - 1].role === 'them')
            start--;
        while (end + 1 < list.length && list[end + 1].role === 'them')
            end++;
        const isLastGroup = end === list.length - 1;
        if (!isLastGroup) {
            setMsgActionsOpen(false);
            return;
        }
        const ok = await confirmDialog({ title: '确认', message: '确定重新生成该条消息？这会覆盖上次回复。', labels: { confirm: '确定', cancel: '取消' } });
        if (!ok)
            return;
        const baseList = list.slice(0, start);
        setMessagesMap({ ...messagesMap, [current.id]: baseList });
        setMsgActionsOpen(false);
        try {
            setTyping(true);
            const historyForServer = buildCompactHistory(baseList);
            const displayName = (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || 'user';
            const uid = getOrCreateUserId();
            const now = new Date();
            const payload = {
                model: (modelName && modelName.trim()) || 'qwen3-7b',
                persona_id: current.id,
                history: historyForServer,
                globals: {
                    user: displayName,
                    user_title: displayName,
                    char: (current?.name ?? ''),
                    persona: (current?.define ?? ''),
                    persona_description: (() => { const a = String(selfDefine || '').trim(); const b = String(selfMemory || '').trim(); return [a, b].filter(Boolean).join('\n'); })(),
                    max_msg_cnt: (() => { const n = Number(maxMsgCnt); return Number.isFinite(n) && n > 0 ? Math.floor(n) : 7; })(),
                    date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
                    time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
                },
                user_id: uid,
                regenerate: true,
            };
            const token = getStoredAuthToken();
            if (!token) {
                setAuthModalOpen(true);
                return;
            }
            await chatAsync.sendRequestLazy(payload);
            // Reply handled asynchronously via WS
        }
        catch {
            // Suppress popup; rely on status banner + auto-reconnect
            setTyping(false);
        }
    };
    React.useEffect(() => { setTyping(false); }, [current?.id, subView]);
    // Sync draft of max_msg_cnt when entering settings or value changes
    React.useEffect(() => {
        const n = Number(maxMsgCnt);
        const v = Number.isFinite(n) && n > 0 ? Math.floor(n) : 7;
        setMaxCntDraft(String(v));
    }, [maxMsgCnt, subView]);
    React.useEffect(() => {
        // Reset counters when switching conversation
        lastCountRef.current = (messages || []).length;
        setHasNew(false);
        requestAnimationFrame(() => scrollToBottom(false));
        setAtBottom(true);
        atBottomRef.current = true;
        cancelPendingTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id]);
    // Connect to chat-async WS as soon as entering the chat app
    React.useEffect(() => {
        chatAsync.connect();
        const unsub = chatAsync.onStatus((s) => setWsStatus(s));
        return () => { try {
            unsub();
        }
        catch { } ; chatAsync.close(); };
    }, []);
    // Cleanup pending timer on unmount
    React.useEffect(() => {
        return () => { try {
            if (pendingTimerRef.current)
                window.clearTimeout(pendingTimerRef.current);
        }
        catch { } ; };
    }, []);
    // If auth state changes (e.g., login provides JWT), connect; manager will recreate only if token changed
    React.useEffect(() => {
        chatAsync.connect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authReady, authUser]);
    // Connect chat-async WebSocket and handle incoming assistant messages
    React.useEffect(() => {
        chatAsync.setHandler((msg) => {
            try {
                if (!msg || msg.type !== 'message')
                    return;
                if (msg.kind === 'pong' || msg.kind === 'system')
                    return;
                if (msg.kind === 'error') {
                    try {
                        notifications.show({ color: 'red', title: '服务异常', message: '聊天服务返回错误', withCloseButton: true });
                    }
                    catch { }
                    setTyping(false);
                    return;
                }
                const payload = msg.payload || {};
                const personaId = (() => {
                    const v = String(payload?.meta?.persona_id || payload?.message?.persona_id || '').trim();
                    return v || null;
                })();
                if (!personaId)
                    return; // 必须依赖服务端 persona_id 归档
                // 若本地不存在该 persona，则创建占位
                const exists = Array.isArray(personas) && personas.some((p) => p.id === personaId);
                if (!exists) {
                    const name = String(payload?.message?.character?.name || '角色');
                    const avatar = 'https://picsum.photos/seed/wa-unknown/64/64';
                    setPersonas((prev) => [{ id: personaId, name, define: '', avatar, last: '新消息', time: '刚刚' }, ...(Array.isArray(prev) ? prev : [])]);
                }
                else {
                    // 更新列表时间显示（右侧时间）
                    setPersonas((prev) => (Array.isArray(prev) ? prev.map((p) => (p.id === personaId ? { ...p, time: '刚刚' } : p)) : prev));
                }
                const text = (() => {
                    const m = payload?.message;
                    if (m && typeof m === 'object') {
                        return (typeof m.text === 'string' ? m.text : (m.reply || m.message || m.content || '')) || '';
                    }
                    return (payload?.text || payload?.reply || payload?.message || '');
                })();
                const replyText = String(text || '');
                const parts = parseReplyToMessages(replyText);
                if (parts.length > 0) {
                    let delayAcc = 0;
                    parts.forEach((chunk, i) => {
                        const gap = Math.floor(600 + Math.random() * 800);
                        delayAcc += gap;
                        window.setTimeout(() => {
                            appendMessageForPersona(personaId, 'them', chunk);
                            if (i === parts.length - 1 && current?.id === personaId)
                                setTyping(false);
                        }, delayAcc);
                    });
                }
                else {
                    const final = replyText.trim();
                    if (final)
                        appendMessageForPersona(personaId, 'them', final);
                    if (current?.id === personaId)
                        setTyping(false);
                }
            }
            catch { }
        });
        return () => { chatAsync.setHandler(null); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id, appendMessageForPersona, personas, setPersonas]);
    // Parse server reply text into multiple assistant messages using phone-style patterns
    function parseReplyToMessages(input) {
        if (!input)
            return [];
        const out = [];
        // Normalize
        let s = String(input || '');
        // Drop HTML comments like <!-- Statements Rejected --> to avoid confusing the parser
        s = s.replace(/<!--[\s\S]*?-->/g, '\n');
        // Normalize line breaks (also unescape common escaped newlines if any slipped through)
        s = s.replace(/[\r\f\v]+/g, '\n').replace(/\\n/g, '\n');
        // Pattern 1: [角色的消息：内容]
        const re1 = /\[[^\]]*?的消息：[\s\S]*?\]/g;
        let m;
        while ((m = re1.exec(s)) !== null) {
            const full = (m[0] || '').trim();
            if (full)
                out.push(full);
        }
        // Pattern 2: [对方消息|avatar|content|time]
        const re2 = /\[对方消息\|[\s\S]*?\]/g;
        while ((m = re2.exec(s)) !== null) {
            const full = (m[0] || '').trim();
            if (full)
                out.push(full);
        }
        // If patterns found, return unique blocks in order
        if (out.length > 0) {
            // Keep stable order but drop exact duplicates
            const seen = new Set();
            const uniq = [];
            for (const it of out) {
                if (!seen.has(it)) {
                    seen.add(it);
                    uniq.push(it);
                }
            }
            return uniq;
        }
        // Fallback A: bracketed lines per line
        const simple = s
            .split(/\n+/)
            .map((line) => String(line || '').trim())
            .filter((ln) => /^\[.*\]$/.test(ln));
        if (simple.length)
            return simple;
        // Fallback B: paragraphs separated by blank lines; attempt to repair missing brackets
        const paras = s
            .split(/\n{2,}/)
            .map((t) => String(t || '').trim())
            .filter(Boolean);
        const repaired = [];
        for (const p of paras) {
            // Skip residual comments just in case
            if (/^<!--[\s\S]*?-->$/.test(p))
                continue;
            if (/^\[.*\]$/.test(p)) {
                repaired.push(p);
                continue;
            }
            // Looks like a message but missing one or both brackets
            if (/^[^\[]*?的消息：[\s\S]*$/.test(p) || /^对方消息\|/.test(p)) {
                const leftFixed = p.startsWith('[') ? p : '[' + p;
                const bothFixed = leftFixed.endsWith(']') ? leftFixed : leftFixed + ']';
                repaired.push(bothFixed);
            }
        }
        return repaired;
    }
    // Removed fetching ST defaults and engine materials
    const exitToHome = React.useCallback(() => {
        startExit(() => setActivePage('home'));
    }, [startExit, setActivePage]);
    // Format helpers
    const unwrapForDisplay = React.useCallback((txt) => {
        const s = String(txt || '').trim();
        // [名字的消息：内容]
        let m = s.match(/^\s*\[(.*?)的消息：([\s\S]*?)\]\s*$/);
        if (m)
            return (m[2] || '').trim();
        // [对方消息|avatar|content|time]
        m = s.match(/^\s*\[对方消息\|[^|\]]*\|([\s\S]*?)\|[^\]]*\]\s*$/);
        if (m)
            return (m[1] || '').trim();
        // Repair: occasionally server text loses a leading '[' but keeps trailing ']'
        m = s.match(/^\s*[^\[]*?的消息：([\s\S]*?)\]\s*$/);
        if (m)
            return (m[1] || '').trim();
        return s;
    }, []);
    const buildCompactHistory = React.useCallback((items) => {
        // Group contiguous 'them' lines into one assistant turn with newline joins; wrap user lines as [name的消息：content]
        const out = [];
        let acc = [];
        const flush = () => { if (acc.length) {
            out.push({ role: 'assistant', content: acc.join('\n') });
            acc = [];
        } };
        const userName = (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || '我';
        const otherName = (current?.name || '对方');
        const isBracket = (t) => /^\s*\[.*\]\s*$/.test(String(t || ''));
        for (const m of items) {
            const raw = String(m.text || '');
            if (m.role === 'me') {
                flush();
                const wrapped = isBracket(raw) ? raw : `[${userName}的消息：${raw}]`;
                out.push({ role: 'user', content: wrapped });
            }
            else {
                const wrapped = isBracket(raw) ? raw : `[${otherName}的消息：${raw}]`;
                acc.push(wrapped);
            }
        }
        flush();
        try {
            console.log('[WA][buildCompactHistory]', { count: out.length, lastRole: out[out.length - 1]?.role, lastPreview: out[out.length - 1]?.content?.slice(0, 100) });
        }
        catch { }
        return out;
    }, [current?.name, nickname, selfNickname]);
    // Build history for server: group contiguous 'them' messages into one assistant turn (joined by \n)
    const buildCompactHistory_dup = React.useCallback((items) => {
        const out = [];
        let acc = [];
        const flush = () => { if (acc.length) {
            out.push({ role: 'assistant', content: acc.join('\n') });
            acc = [];
        } };
        for (const m of items) {
            const txt = String(m.text || '');
            if (m.role === 'me') {
                flush();
                out.push({ role: 'user', content: txt });
            }
            else {
                acc.push(txt);
            }
        }
        flush();
        try {
            console.log('[WA][buildCompactHistory]', { count: out.length, lastRole: out[out.length - 1]?.role, lastPreview: out[out.length - 1]?.content?.slice(0, 100) });
        }
        catch { }
        return out;
    }, []);
    // Auto summary when history grows large
    React.useEffect(() => {
        if (!current)
            return;
        const personaId = current.id;
        const list = (messagesMap?.[personaId] || []);
        const totalLen = list.reduce((acc, m) => acc + (m?.text ? String(m.text).length : 0), 0);
        const count = list.length;
        const THRESHOLD_LEN = 2000;
        const THRESHOLD_COUNT = 40;
        const prev = summaryMetaRef.current[personaId] || { len: 0, count: 0, ts: 0 };
        const crossed = (!summaryLoading && !summaryModalOpen) && ((prev.len < THRESHOLD_LEN && totalLen >= THRESHOLD_LEN) || (prev.count < THRESHOLD_COUNT && count >= THRESHOLD_COUNT));
        if (!crossed)
            return;
        // Build plain history for summary
        const unwrap = (txt) => {
            const s = String(txt || '').trim();
            let m = s.match(/^\s*\[(.*?)的消息：([\s\S]*?)\]\s*$/);
            if (m)
                return (m[2] || '').trim();
            m = s.match(/^\s*\[对方消息\|[^|\]]*\|([\s\S]*?)\|[^\]]*\]\s*$/);
            if (m)
                return (m[1] || '').trim();
            return s;
        };
        const plainHistory = list.map((m) => ({ role: m.role === 'me' ? 'user' : 'assistant', content: unwrap(m.text || '') }));
        (async () => {
            try {
                setSummaryLoading(true);
                const base = getApiBase();
                const uid = getOrCreateUserId();
                const token = getStoredAuthToken();
                if (!token) {
                    setAuthModalOpen(true);
                    setSummaryLoading(false);
                    return;
                }
                const body = {
                    persona_id: personaId,
                    user_id: uid,
                    history: plainHistory,
                    globals: {
                        user: (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || 'user',
                        user_title: (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || 'user',
                        char: (current?.name ?? ''),
                        persona: (current?.define ?? ''),
                        persona_description: (() => { const a = String(selfDefine || '').trim(); const b = String(selfMemory || '').trim(); return [a, b].filter(Boolean).join('\n'); })(),
                    },
                };
                const res = await httpFetch(`${base}/api/chat/summary`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body), timeoutMs: 15000 });
                let sum = '';
                try {
                    const data = await res.json();
                    sum = String(data?.summary || data?.text || data?.result || '');
                }
                catch {
                    sum = '';
                }
                if (!sum) {
                    try {
                        sum = await res.text();
                    }
                    catch { }
                }
                setSummaryDraft(String(sum || ''));
                setSummaryModalOpen(true);
                setSummaryLoading(false);
                summaryMetaRef.current[personaId] = { len: totalLen, count, ts: Date.now() };
            }
            catch (e) {
                setSummaryLoading(false);
            }
        })();
    }, [current?.id, messagesMap]);
    const send = async () => {
        const text = draft.trim();
        if (!text)
            return;
        appendMessage('me', text);
        setDraft('');
        // After user sends a new message, auto-scroll to bottom so they see latest
        try {
            requestAnimationFrame(() => { scrollToBottom(true); setAtBottom(true); setHasNew(false); });
        }
        catch { }
        // Debounced server send: wait 3s of no input then send
        restartPendingTimer();
    };
    // Create persona on server, then persist locally
    const createPersonaRemote = React.useCallback(async (name, define) => {
        const n = String(name || '').trim();
        const d = String(define || '').trim();
        if (!n || !d)
            return false;
        try {
            const token = getStoredAuthToken();
            if (!token) {
                setAuthModalOpen(true);
                return false;
            }
            const base = getApiBase();
            const body = { name: n, description: d, descrption: d };
            const res = await httpFetch(`${base}/api/char/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
                timeoutMs: 15000,
            });
            const data = await res.json();
            const serverId = (data && (data.id || data?.persona?.id)) ? String(data.id || data?.persona?.id) : null;
            const id = serverId || `p${Date.now()}`;
            const avatar = `https://picsum.photos/seed/${encodeURIComponent(n + id)}/64/64`;
            const persona = { id, name: n, define: d, avatar, last: '新的人设已创建', time: '刚刚' };
            setPersonas((list) => [persona, ...(Array.isArray(list) ? list : [])]);
            notifications.show({ title: '创建成功', message: '人设已创建并保存', color: 'green' });
            return true;
        }
        catch (e) {
            notifications.show({ title: '创建失败', message: e?.message || '无法创建人设', color: 'red' });
            return false;
        }
    }, [setPersonas, setAuthModalOpen]);
    // Removed inference and provider classes
    return (_jsxs("div", { style: containerStyle, children: [_jsx("div", { "aria-hidden": true, style: underlayStyle }), _jsxs("div", { style: panelStyle, children: [_jsx("div", { className: "top-safe-gap-0", "aria-hidden": true }), _jsxs("div", { style: { height: 56, display: 'flex', alignItems: 'center', padding: '0 8px', gap: 8, borderBottom: '1px solid var(--app-border)' }, children: [_jsx(ActionIcon, { variant: "light", radius: "xl", onClick: () => {
                                    if (current) {
                                        // When inside a conversation, back to chats list
                                        if (subView !== 'conversation') {
                                            setSubView('conversation');
                                        }
                                        else {
                                            setCurrentId(null);
                                            setTab('messages');
                                        }
                                    }
                                    else {
                                        // At root of app, exit to home
                                        exitToHome();
                                    }
                                }, "aria-label": "\u8FD4\u56DE", children: _jsx(IconArrowLeft, { size: 18 }) }), _jsxs("div", { style: { flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }, children: [_jsx(Text, { fw: 800, size: "sm", children: current
                                            ? (subView === 'settings' ? '会话设置' : subView === 'personaEdit' ? '编辑人设' : current.name)
                                            : (tab === 'feeds' ? (subView === 'feedComposer' ? '发布动态' : '动态') : 'WhatsApp') }), current && subView === 'conversation' && typing && (_jsx(Text, { size: "xs", c: "dimmed", style: { marginTop: 2 }, children: "\u5BF9\u65B9\u6B63\u5728\u8F93\u5165\u2026" }))] }), current && subView === 'conversation' ? (_jsx(ActionIcon, { variant: "light", radius: "xl", "aria-label": "\u4F1A\u8BDD\u8BBE\u7F6E", onClick: () => setSubView('settings'), children: _jsx(IconSettings, { size: 18 }) })) : tab === 'messages' ? (_jsx(ActionIcon, { variant: "light", radius: "xl", "aria-label": "\u6DFB\u52A0\u4EBA\u8BBE", onClick: () => { setNewName(''); setNewDefine(''); setAddOpen(true); }, children: _jsx(IconPlus, { size: 18 }) })) : (!current && tab === 'feeds' && subView !== 'feedComposer') ? (_jsx(ActionIcon, { variant: "light", radius: "xl", "aria-label": "\u53D1\u5E03\u52A8\u6001", onClick: () => setSubView('feedComposer'), children: _jsx(IconPlus, { size: 18 }) })) : (_jsx("div", { style: { width: 32 } }))] }), _jsxs("div", { style: { flex: 1, minHeight: 0, position: 'relative' }, children: [tab === 'messages' && !current && (_jsx(Stack, { p: "sm", gap: "xs", style: { height: '100%', overflow: 'auto' }, children: personas.map((c) => (_jsx(Card, { withBorder: true, radius: "md", p: "sm", onClick: () => { setCurrentId(c.id); }, style: { cursor: 'pointer', background: 'var(--app-bg-secondary)', borderColor: 'var(--app-border)' }, children: _jsxs(Group, { justify: "space-between", align: "center", children: [_jsxs(Group, { gap: 12, align: "center", children: [_jsx("img", { src: c.avatar, width: 44, height: 44, style: { borderRadius: '50%', objectFit: 'cover' } }), _jsxs(Stack, { gap: 2, style: { maxWidth: '60vw' }, children: [_jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: c.name }), _jsx(Text, { size: "xs", c: "dimmed", style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: (() => { const arr = messagesMap?.[c.id]; return arr && arr.length ? (arr[arr.length - 1].text || '') : ''; })() })] })] }), _jsx(Text, { size: "xs", c: "dimmed", children: c.time })] }) }, c.id))) })), current && subView === 'conversation' && (_jsxs("div", { style: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }, children: [_jsx("div", { "aria-hidden": true, style: { position: 'absolute', inset: 0, zIndex: 0,
                                            background: (current && wallpapers?.[current.id] && String(wallpapers[current.id]).trim())
                                                ? `url(${wallpapers[current.id]}) center/cover no-repeat`
                                                : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark-theme') ? '#000' : '#fff')
                                        } }), _jsxs("div", { ref: msgsScrollRef, onScroll: onScrollMsgs, style: { flex: 1, minHeight: 0, overflow: 'auto', padding: '12px 12px 0', position: 'relative', zIndex: 1 }, children: [wsStatus.state !== 'open' && (_jsx("div", { style: { position: 'sticky', top: 0, zIndex: 3, marginBottom: 8 }, children: _jsx(Card, { withBorder: true, radius: "md", p: "xs", style: { background: 'var(--app-bg-secondary)', borderColor: 'var(--app-border)' }, children: _jsxs(Group, { justify: "space-between", gap: "sm", wrap: "nowrap", children: [_jsx(Text, { size: "sm", c: "dimmed", children: wsStatus.state === 'connecting' ? '正在连接…' : wsStatus.state === 'reconnecting' ? `正在重连（第 ${wsStatus.attempts} 次）…` : '连接已断开' }), wsStatus.state === 'closed' && (_jsx(Button, { size: "xs", variant: "light", onClick: () => chatAsync.retryNow(), children: "\u91CD\u8BD5" }))] }) }) })), _jsxs(Stack, { gap: 8, children: [messages.map((m, idx) => {
                                                        const cur = new Date(m.ts || Date.now());
                                                        const prev = idx > 0 ? new Date(messages[idx - 1].ts || Date.now()) : null;
                                                        const newDay = !prev || cur.toDateString() !== prev.toDateString();
                                                        const dateLabel = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
                                                        const timeLabel = `${String(cur.getHours()).padStart(2, '0')}:${String(cur.getMinutes()).padStart(2, '0')}`;
                                                        const isHiddenComment = typeof m.text === 'string' && /^\s*<!--[\s\S]*?-->\s*$/.test(m.text);
                                                        if (isHiddenComment)
                                                            return null;
                                                        return (_jsxs(React.Fragment, { children: [newDay && (_jsx("div", { style: { display: 'flex', justifyContent: 'center', margin: '6px 0' }, children: _jsx(Text, { size: "xs", c: "dimmed", children: dateLabel }) })), _jsxs("div", { style: { display: 'flex', justifyContent: m.role === 'me' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 6 }, children: [m.role === 'me' && (_jsx(Text, { size: "xs", c: "dimmed", children: timeLabel })), _jsx("div", { onMouseDown: () => lpStart(idx, m.role), onMouseUp: lpCancel, onMouseLeave: lpCancel, onTouchStart: () => lpStart(idx, m.role), onTouchEnd: lpCancel, style: {
                                                                                maxWidth: '78%',
                                                                                padding: '10px 14px',
                                                                                borderRadius: 16,
                                                                                background: chatStyle === 'glass'
                                                                                    ? (m.role === 'me' ? 'rgba(135,206,250,0.85)' : 'rgba(255,255,255,0.7)')
                                                                                    : (m.role === 'me' ? '#87CEFA' : 'rgba(255,255,255,0.92)'),
                                                                                color: m.role === 'me' ? '#fff' : '#111',
                                                                                boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                                                                                border: m.role === 'me' ? '1px solid rgba(255,255,255,0.18)' : 'none',
                                                                                backdropFilter: chatStyle === 'glass' ? 'blur(8px) saturate(120%)' : undefined
                                                                            }, children: editingRange && idx >= editingRange.start && idx <= editingRange.end ? (_jsxs("div", { children: [_jsx(Textarea, { autosize: true, minRows: 2, maxRows: 8, value: editingDraftMsg, onChange: (e) => setEditingDraftMsg(e.currentTarget.value) }), _jsxs(Group, { gap: 8, mt: 6, justify: m.role === 'me' ? 'flex-end' : 'flex-start', children: [_jsx(Button, { size: "xs", onClick: saveEdit, children: "\u4FDD\u5B58" }), _jsx(Button, { size: "xs", variant: "light", onClick: cancelEdit, children: "\u53D6\u6D88" })] })] })) : (_jsx(Text, { size: "sm", style: { whiteSpace: 'pre-wrap' }, children: unwrapForDisplay(m.text) })) }), m.role !== 'me' && (_jsx(Text, { size: "xs", c: "dimmed", children: timeLabel }))] })] }, m.id));
                                                    }), hasNew && !atBottom && (_jsx("div", { style: { position: 'sticky', bottom: 8, display: 'flex', justifyContent: 'center', zIndex: 3 }, children: _jsx("div", { role: "button", onClick: () => { scrollToBottom(true); setHasNew(false); }, style: { background: '#fff7cc', border: '1px solid #e0c200', color: '#111', padding: '6px 12px', borderRadius: 16, cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.12)' }, children: "\u60A8\u6709\u65B0\u7684\u6D88\u606F" }) }))] }), summaryLoading && (_jsx("div", { style: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', zIndex: 4 }, children: _jsx("div", { style: { padding: '10px 14px', borderRadius: 12, background: '#fff', color: '#111', boxShadow: '0 8px 22px rgba(0,0,0,0.2)', border: '1px solid var(--app-border)' }, children: "AI \u603B\u7ED3\u4E2D\u2026" }) }))] }), _jsx(Modal, { opened: msgActionsOpen, onClose: () => setMsgActionsOpen(false), centered: true, withCloseButton: true, title: "\u6D88\u606F\u64CD\u4F5C", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Button, { fullWidth: true, onClick: () => { if (msgActionsIndex != null)
                                                        beginEdit(msgActionsIndex); }, children: "\u7F16\u8F91" }), _jsx(Button, { color: "red", variant: "light", fullWidth: true, onClick: () => { if (msgActionsIndex != null)
                                                        deleteAt(msgActionsIndex); }, children: "\u5220\u9664" }), msgActionsRole === 'them' && current && (msgActionsIndex === ((messagesMap[current.id] || []).length - 1)) && (_jsx(Button, { variant: "light", fullWidth: true, onClick: () => { if (msgActionsIndex != null)
                                                        regenerateAt(msgActionsIndex); }, children: "\u91CD\u65B0\u751F\u6210" }))] }) }), _jsx("div", { style: {
                                            padding: '8px 12px',
                                            borderTop: '1px solid transparent',
                                            background: 'transparent',
                                            position: 'relative',
                                            zIndex: 2,
                                            paddingBottom: 'calc(8px + max(env(safe-area-inset-bottom, 0px), var(--sab, 0px)))',
                                        }, children: _jsxs(Group, { align: "flex-end", gap: 8, wrap: "nowrap", children: [_jsx(ActionIcon, { variant: "subtle", radius: "xl", onClick: () => setShowActions((v) => !v), "aria-label": "\u66F4\u591A", style: { boxShadow: '0 6px 18px rgba(0,0,0,0.18)' }, children: _jsx(IconPlus, { size: 18 }) }), _jsx(Textarea, { value: draft, onChange: (e) => { setDraft(e.currentTarget.value); if (pendingTimerRef.current)
                                                        restartPendingTimer(); }, onKeyDown: (e) => { if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        send();
                                                    } }, id: textareaId, minRows: 1, maxRows: 5, autosize: true, flex: 1, placeholder: "\u8F93\u5165\u6D88\u606F", variant: "filled", radius: "xl", styles: {
                                                        input: (typeof document !== 'undefined' && document.documentElement.classList.contains('dark-theme'))
                                                            ? {
                                                                background: 'rgba(255,255,255,0.08)',
                                                                borderColor: 'rgba(255,255,255,0.16)',
                                                                color: 'inherit',
                                                                backdropFilter: 'blur(6px) saturate(120%)',
                                                                WebkitBackdropFilter: 'blur(6px) saturate(120%)',
                                                                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                                                            }
                                                            : {
                                                                background: 'rgba(255,255,255,0.92)',
                                                                borderColor: 'rgba(0,0,0,0.10)',
                                                                color: '#111',
                                                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                                                            }
                                                    } }), _jsxs(Popover, { opened: emojiOpen, onChange: setEmojiOpen, position: "top-end", withArrow: true, shadow: "md", children: [_jsx(Popover.Target, { children: _jsx(ActionIcon, { variant: "subtle", radius: "xl", "aria-label": "\u8868\u60C5", style: { boxShadow: '0 6px 18px rgba(0,0,0,0.18)' }, onClick: () => setEmojiOpen((v) => !v), children: _jsx(IconMoodSmile, { size: 20 }) }) }), _jsx(Popover.Dropdown, { style: { padding: 8, borderRadius: 12 }, children: _jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, maxWidth: 320 }, children: EMOJIS.map((e) => (_jsx("button", { onClick: () => insertEmoji(e), style: { fontSize: 20, lineHeight: 1.2, background: 'transparent', border: 'none', cursor: 'pointer' }, "aria-label": e, children: e }, e))) }) })] }), _jsx(ActionIcon, { variant: "filled", radius: "xl", size: "lg", onClick: send, disabled: !draft.trim(), "aria-label": "\u53D1\u9001", style: { boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }, children: _jsx(IconMessageDots, { size: 18 }) })] }) }), showActions && (_jsxs("div", { style: { padding: '12px 12px 16px', background: 'var(--app-bg-secondary)', borderTop: '1px solid var(--app-border)', zIndex: 2 }, children: [_jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 8 }, children: [
                                                    { icon: _jsx(IconPhoto, { size: 22 }), label: '照片' },
                                                    { icon: _jsx(IconCamera, { size: 22 }), label: '相机' },
                                                    { icon: _jsx(IconFile, { size: 22 }), label: '文件' },
                                                    { icon: _jsx(IconMapPin, { size: 22 }), label: '位置' },
                                                ].map((it, idx) => (_jsxs(Card, { withBorder: true, radius: "md", p: "sm", style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', background: 'var(--app-bg-secondary)', borderColor: 'var(--app-border)' }, children: [it.icon, _jsx(Text, { size: "xs", c: "dimmed", children: it.label })] }, `row1-${idx}`))) }), _jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }, children: [
                                                    { icon: _jsx(IconUser, { size: 22 }), label: '联系人' },
                                                    { icon: _jsx(IconMicrophone, { size: 22 }), label: '录音' },
                                                    { icon: _jsx(IconMusic, { size: 22 }), label: '音频' },
                                                    { icon: _jsx(IconSticker2, { size: 22 }), label: '贴纸' },
                                                ].map((it, idx) => (_jsxs(Card, { withBorder: true, radius: "md", p: "sm", style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', background: 'var(--app-bg-secondary)', borderColor: 'var(--app-border)' }, children: [it.icon, _jsx(Text, { size: "xs", c: "dimmed", children: it.label })] }, `row2-${idx}`))) })] }))] })), tab === 'feeds' && !current && subView !== 'feedComposer' && (_jsx("div", { style: { height: '100%', overflow: 'hidden' }, children: _jsxs(Stack, { p: "md", gap: "md", style: { height: '100%', overflow: 'auto' }, children: [(!feeds || feeds.length === 0) && (_jsxs(Stack, { align: "center", justify: "center", gap: "sm", style: { padding: '60px 0' }, children: [_jsx(Text, { c: "dimmed", children: "\u8FD8\u6CA1\u6709\u4EFB\u4F55\u52A8\u6001" }), _jsx(Text, { size: "sm", c: "dimmed", children: "\u70B9\u51FB\u53F3\u4E0A\u89D2 + \u53D1\u5E03\u4F60\u7684\u7B2C\u4E00\u6761\u52A8\u6001" })] })), (feeds || []).map((f) => (_jsxs(Card, { withBorder: true, radius: "md", p: "sm", children: [_jsxs(Group, { gap: "sm", align: "center", children: [_jsx("img", { src: f.avatar || 'https://picsum.photos/seed/u/40/40', width: 36, height: 36, style: { borderRadius: '50%', objectFit: 'cover' } }), _jsxs(Stack, { gap: 0, style: { flex: 1 }, children: [_jsx(Text, { fw: 600, children: f.userName }), _jsx(Text, { size: "xs", c: "dimmed", children: new Date(f.ts).toLocaleString() })] }), f.mine && (_jsx(ActionIcon, { variant: "subtle", color: "red", onClick: async () => {
                                                                const ok = await confirmDialog({ title: '删除动态', message: '确认删除该动态？', labels: { confirm: '删除', cancel: '取消' } });
                                                                if (!ok)
                                                                    return;
                                                                setFeeds((prev) => (prev || []).filter((x) => x.id !== f.id));
                                                                try {
                                                                    const tk = getStoredAuthToken();
                                                                    if (tk) {
                                                                        const base = getApiBase();
                                                                        await httpFetch(`${base}/api/feeds/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ id: f.id }) });
                                                                    }
                                                                }
                                                                catch { }
                                                            }, children: _jsx(IconTrash, { size: 16 }) }))] }), f.text && _jsx(Text, { mt: 6, style: { whiteSpace: 'pre-wrap' }, children: f.text }), Array.isArray(f.images) && f.images.length > 0 && (_jsx("div", { style: { display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, f.images.length)}, 1fr)`, gap: 6, marginTop: 6 }, children: f.images.map((img, i) => (_jsx("img", { src: img, style: { width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 } }, i))) })), _jsxs(Group, { mt: 8, gap: "md", children: [_jsx(ActionIcon, { variant: "subtle", onClick: async () => {
                                                                setFeeds((prev) => (prev || []).map((x) => x.id === f.id ? { ...x, liked: !x.liked, likes: Math.max(0, x.likes + (x.liked ? -1 : 1)) } : x));
                                                                try {
                                                                    const tk = getStoredAuthToken();
                                                                    if (tk) {
                                                                        const base = getApiBase();
                                                                        await httpFetch(`${base}/api/feeds/like`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ id: f.id, like: !f.liked }) });
                                                                    }
                                                                }
                                                                catch { }
                                                            }, "aria-label": "\u70B9\u8D5E", children: f.liked ? _jsx(IconHeartFilled, { size: 18, color: "#e03131" }) : _jsx(IconHeart, { size: 18 }) }), _jsx(Text, { size: "sm", children: f.likes }), _jsx(ActionIcon, { variant: "subtle", onClick: async () => {
                                                                setFeeds((prev) => (prev || []).map((x) => x.id === f.id ? { ...x, reposts: x.reposts + 1 } : x));
                                                                try {
                                                                    const tk = getStoredAuthToken();
                                                                    if (tk) {
                                                                        const base = getApiBase();
                                                                        await httpFetch(`${base}/api/feeds/repost`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ id: f.id }) });
                                                                    }
                                                                }
                                                                catch { }
                                                            }, "aria-label": "\u8F6C\u53D1", children: _jsx(IconRepeat, { size: 18 }) }), _jsx(Text, { size: "sm", children: f.reposts }), _jsx(ActionIcon, { variant: "subtle", onClick: () => { setCommentTargetId(f.id); setCommentDraft(''); setCommentModalOpen(true); }, "aria-label": "\u8BC4\u8BBA", children: _jsx(IconMessageDots, { size: 18 }) }), _jsx(Text, { size: "sm", children: (f.comments || []).length })] }), (f.comments || []).length > 0 && (_jsx(Stack, { mt: 8, gap: 6, children: f.comments.map((c) => (_jsxs(Group, { gap: 6, align: "flex-start", children: [_jsx("img", { src: c.avatar || 'https://picsum.photos/seed/com/28/28', width: 28, height: 28, style: { borderRadius: '50%', objectFit: 'cover' } }), _jsxs(Stack, { gap: 2, style: { flex: 1 }, children: [_jsxs(Group, { gap: 6, children: [_jsx(Text, { size: "sm", fw: 600, children: c.userName }), _jsx(Text, { size: "xs", c: "dimmed", children: new Date(c.ts).toLocaleString() })] }), _jsx(Text, { size: "sm", style: { whiteSpace: 'pre-wrap' }, children: c.text })] })] }, c.id))) }))] }, f.id)))] }) })), tab === 'feeds' && !current && subView === 'feedComposer' && (_jsx("div", { style: { height: '100%', overflow: 'hidden' }, children: _jsx(Stack, { p: "md", gap: "md", style: { height: '100%', overflow: 'auto' }, children: _jsx(Card, { withBorder: true, radius: "md", p: "sm", children: _jsxs(Group, { align: "flex-start", gap: "sm", children: [_jsx("img", { src: avatar || 'https://picsum.photos/seed/me/40/40', width: 40, height: 40, style: { borderRadius: '50%', objectFit: 'cover' } }), _jsxs(Stack, { gap: 6, style: { flex: 1 }, children: [_jsx(Textarea, { placeholder: "\u8FD9\u4E00\u523B\u7684\u60F3\u6CD5\u2026", minRows: 4, autosize: true, id: "wa-feed-draft", styles: { input: { background: 'var(--app-bg-secondary)' } } }), _jsxs(Group, { gap: "sm", align: "center", children: [_jsx("input", { type: "file", accept: "image/*", multiple: true, id: "wa-feed-files" }), _jsx(Button, { leftSection: _jsx(IconSend, { size: 16 }), onClick: async () => {
                                                                        const textEl = document.getElementById('wa-feed-draft');
                                                                        const filesEl = document.getElementById('wa-feed-files');
                                                                        const text = (textEl?.value || '').trim();
                                                                        const files = Array.from(filesEl?.files || []);
                                                                        if (!text && files.length === 0)
                                                                            return;
                                                                        const imgs = [];
                                                                        for (const f of files.slice(0, 6)) {
                                                                            const url = await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(String(r.result || '')); r.readAsDataURL(f); });
                                                                            imgs.push(url);
                                                                        }
                                                                        const meName = (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || '我';
                                                                        const myFeed = { id: `feed_${Date.now()}`, userName: meName, avatar: avatar || undefined, text, images: imgs, ts: Date.now(), likes: 0, liked: false, reposts: 0, comments: [], mine: true };
                                                                        setFeeds((prev) => [myFeed, ...(Array.isArray(prev) ? prev : [])]);
                                                                        try {
                                                                            const token = getStoredAuthToken();
                                                                            if (token) {
                                                                                const base = getApiBase();
                                                                                await httpFetch(`${base}/api/feeds/create`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ text, images: imgs }), timeoutMs: 15000 });
                                                                            }
                                                                            else {
                                                                                setAuthModalOpen(true);
                                                                            }
                                                                        }
                                                                        catch { }
                                                                        if (textEl)
                                                                            textEl.value = '';
                                                                        if (filesEl)
                                                                            filesEl.value = '';
                                                                        setSubView('conversation');
                                                                    }, children: "\u53D1\u5E03" })] })] })] }) }) }) })), tab === 'me' && !current && (_jsxs(Stack, { p: "md", gap: "md", style: { height: '100%', overflow: 'auto' }, children: [_jsxs(Group, { align: "center", gap: "md", children: [_jsx("div", { style: { width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: '#eee' }, children: avatar ? (_jsx("img", { src: avatar, width: 64, height: 64, style: { objectFit: 'cover' } })) : (_jsx("div", { style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }, children: "\u65E0\u5934\u50CF" })) }), _jsxs("div", { children: [_jsx("input", { type: "file", accept: "image/*", onChange: (e) => {
                                                            const f = e.currentTarget.files?.[0];
                                                            if (!f)
                                                                return;
                                                            const reader = new FileReader();
                                                            reader.onload = () => setAvatar(String(reader.result || ''));
                                                            reader.readAsDataURL(f);
                                                        } }), _jsx(Text, { size: "xs", c: "dimmed", children: "\u66F4\u6362\u5934\u50CF" })] })] }), _jsx(TextInput, { label: "\u6635\u79F0", placeholder: "\u8F93\u5165\u6635\u79F0", value: nickname, onChange: (e) => setNickname(e.currentTarget.value) }), _jsx(TextInput, { label: "\u72B6\u6001", placeholder: "\u4F8B\u5982\uFF1A\u5728\u7EBF\u3001\u5FD9\u788C\u3001\u79BB\u5F00\u2026", value: status, onChange: (e) => setStatus(e.currentTarget.value) }), _jsx(Divider, { label: "\u804A\u5929\u5916\u89C2" }), _jsx(Select, { label: "\u98CE\u683C", placeholder: "\u9009\u62E9\u98CE\u683C", value: chatStyle, onChange: (v) => setChatStyle(v || 'default'), data: [
                                            { value: 'default', label: '默认' },
                                            { value: 'glass', label: '玻璃风' },
                                            { value: 'compact', label: '极简' },
                                        ] }), _jsx(Divider, { label: "\u504F\u597D\u8BBE\u7F6E" }), _jsxs(Group, { justify: "space-between", align: "center", children: [_jsx(Text, { children: "\u663E\u793A\u5DF2\u8BFB\u56DE\u6267" }), _jsx(Switch, { checked: prefs.readReceipts, onChange: (e) => setPrefs({ ...prefs, readReceipts: e.currentTarget.checked }) })] }), _jsxs(Group, { justify: "space-between", align: "center", children: [_jsx(Text, { children: "\u9707\u52A8\u63D0\u9192" }), _jsx(Switch, { checked: prefs.vibrate, onChange: (e) => setPrefs({ ...prefs, vibrate: e.currentTarget.checked }) })] }), _jsx(Divider, { label: "\u6A21\u578B\u914D\u7F6E" }), _jsx(Select, { label: "\u9009\u62E9\u6A21\u578B", placeholder: models && models.length ? '选择模型' : '加载模型列表中…', data: (models || []).map((m) => ({ value: m.model, label: m.name || m.model })), value: modelName, onChange: (v) => setModelName(v || modelName), searchable: true, nothingFoundMessage: "\u65E0\u6A21\u578B" }), _jsx(Group, { justify: "flex-end", children: _jsx(Button, { color: "red", variant: "light", onClick: async () => {
                                                const ok = await confirmDialog({ title: '确认', message: '清理后数据不可恢复', labels: { confirm: '确定', cancel: '取消' } });
                                                if (!ok)
                                                    return;
                                                try {
                                                    // 清理本地聊天数据
                                                    setPersonas([]);
                                                    setMessagesMap({});
                                                    setCurrentId(null);
                                                    setInitDone(false);
                                                    notifications.show({ title: '已清理', message: '已清除 WhatsApp 本地角色与消息' });
                                                }
                                                catch { }
                                                // 同步通知服务端清理（协议与 /api/personas/init 类似）
                                                try {
                                                    const token = getStoredAuthToken();
                                                    if (!token)
                                                        return; // 未登录则仅清理本地
                                                    const base = getApiBase();
                                                    await httpFetch(`${base}/api/personas/clean`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                    });
                                                }
                                                catch (e) {
                                                    // 静默失败，避免影响本地清理体验
                                                    try {
                                                        console.warn('[WA] personas.clean failed', e);
                                                    }
                                                    catch { }
                                                }
                                            }, children: "\u6E05\u7406\u804A\u5929\u6570\u636E" }) })] })), current && subView === 'settings' && (_jsxs(Stack, { p: "md", gap: "sm", style: { height: '100%', overflow: 'auto' }, children: [_jsx(Text, { size: "sm", c: "dimmed", children: "\u5BF9\u5F53\u524D\u4F1A\u8BDD\u8FDB\u884C\u8BBE\u7F6E" }), _jsx(Divider, { label: "\u6211\u7684\u4FE1\u606F" }), _jsx(TextInput, { label: "\u6211\u7684\u6635\u79F0", placeholder: "\u7528\u4E8E\u5BF9\u8BDD\u4E2D\u7684\u79F0\u547C", value: selfNickname || '', onChange: (e) => setSelfNickname(e.currentTarget.value) }), _jsx(Textarea, { label: "\u6211\u7684\u4EBA\u8BBE", placeholder: "\u63CF\u8FF0\u4F60\u7684\u6027\u683C/\u80CC\u666F/\u504F\u597D\uFF08\u5C06\u4F5C\u4E3A persona_description \u4F20\u7ED9\u670D\u52A1\u7AEF\uFF09", minRows: 4, autosize: true, value: selfDefine || '', onChange: (e) => setSelfDefine(e.currentTarget.value) }), _jsx(Textarea, { label: "\u89D2\u8272\u8BB0\u5FC6", placeholder: "\u7528\u4E8E\u957F\u671F\u8BB0\u5FC6\u4E0E\u504F\u597D\uFF08\u5C06\u4E0E\u6211\u7684\u4EBA\u8BBE\u4EE5\u56DE\u8F66\u8FDE\u63A5\u4E00\u5E76\u4F20\u7ED9\u670D\u52A1\u7AEF\uFF09", minRows: 4, autosize: true, value: selfMemory || '', onChange: (e) => setSelfMemory(e.currentTarget.value) }), _jsx(TextInput, { label: "\u6700\u591A\u56DE\u590D\u6761\u6570", placeholder: "\u9ED8\u8BA4 7", type: "number", value: maxCntDraft, onChange: (e) => setMaxCntDraft(e.currentTarget.value), onBlur: () => {
                                            const v = parseInt((maxCntDraft || '').trim(), 10);
                                            const nv = Number.isFinite(v) && v > 0 ? v : 7;
                                            setMaxMsgCnt(nv);
                                            setMaxCntDraft(String(nv));
                                        } }), _jsx(Button, { variant: "light", onClick: () => { setNickDraft(current.name); setNickModalOpen(true); }, children: "\u4FEE\u6539\u6635\u79F0" }), _jsx(Button, { variant: "light", onClick: () => setSubView('personaEdit'), children: "\u7F16\u8F91\u4EBA\u8BBE" }), _jsx(Button, { variant: "light", onClick: () => wallpaperFileRef.current?.click(), children: "\u8BBE\u7F6E\u58C1\u7EB8" }), _jsx(Button, { variant: "subtle", color: "red", onClick: () => { if (current) {
                                            const id = current.id;
                                            setWallpapers((prev) => { const n = { ...(prev || {}) }; delete n[id]; return n; });
                                            notifications.show({ message: '已移除壁纸' });
                                        } }, children: "\u79FB\u9664\u58C1\u7EB8" }), _jsx("input", { ref: wallpaperFileRef, type: "file", accept: "image/*", style: { display: 'none' }, onChange: (e) => {
                                            const f = e.currentTarget.files?.[0];
                                            e.currentTarget.value = '';
                                            if (!f)
                                                return;
                                            const reader = new FileReader();
                                            reader.onload = () => {
                                                const url = String(reader.result || '');
                                                if (current) {
                                                    const id = current.id;
                                                    setWallpapers((prev) => ({ ...(prev || {}), [id]: url }));
                                                }
                                                setSubView('conversation');
                                                notifications.show({ message: '壁纸已设置' });
                                            };
                                            reader.readAsDataURL(f);
                                        } })] })), current && subView === 'personaEdit' && (_jsxs(Stack, { p: "md", gap: "sm", style: { height: '100%', overflow: 'auto' }, children: [_jsx(Text, { size: "sm", c: "dimmed", children: "\u7F16\u8F91\u4EBA\u8BBE\uFF08\u957F\u6587\u672C\uFF09" }), _jsx(Textarea, { minRows: 10, autosize: true, value: current.define, onChange: (e) => {
                                            const define = e.currentTarget.value;
                                            setPersonas((list) => list.map((p) => p.id === current.id ? { ...p, define } : p));
                                        } }), _jsx(Group, { justify: "flex-end", children: _jsx(Button, { onClick: () => setSubView('conversation'), children: "\u5B8C\u6210" }) })] }))] }), _jsx("div", { style: { height: 56, borderTop: '1px solid var(--app-border)', background: 'var(--app-bg-secondary)' }, children: _jsxs(Group, { justify: "space-around", align: "center", style: { height: '100%' }, children: [_jsxs("div", { onClick: () => { setCurrentId(null); setTab('messages'); }, style: { textAlign: 'center', cursor: 'pointer' }, children: [_jsx(IconMessageDots, { size: 20 }), _jsx(Text, { size: "xs", c: tab === 'messages' ? 'blue' : '#000', children: "\u6D88\u606F" })] }), _jsxs("div", { onClick: () => { setCurrentId(null); setTab('feeds'); }, style: { textAlign: 'center', cursor: 'pointer' }, children: [_jsx(IconPhoto, { size: 20 }), _jsx(Text, { size: "xs", c: tab === 'feeds' ? 'blue' : '#000', children: "\u52A8\u6001" })] }), _jsxs("div", { onClick: () => { setCurrentId(null); setTab('me'); }, style: { textAlign: 'center', cursor: 'pointer' }, children: [_jsx(IconUser, { size: 20 }), _jsx(Text, { size: "xs", c: tab === 'me' ? 'blue' : '#000', children: "\u6211" })] })] }) })] }), _jsx(Modal, { opened: addOpen, onClose: () => setAddOpen(false), centered: true, title: _jsx("div", { style: { width: '100%', textAlign: 'center', color: 'var(--app-text-primary)' }, children: "\u6DFB\u52A0\u65B0\u7684\u4EBA\u8BBE" }), ...modalCommonProps, children: _jsxs(Stack, { gap: "md", align: "center", children: [_jsx(TextInput, { placeholder: "\u8F93\u5165\u4EBA\u8BBE\u540D\u79F0", value: newName, onChange: (e) => setNewName(e.currentTarget.value), styles: { input: { textAlign: 'center', backgroundColor: '#f5f6f7', color: 'var(--app-text-primary)' } } }), _jsx(Textarea, { id: "wa-new-define", minRows: 6, autosize: true, placeholder: "\u8F93\u5165\u4EBA\u8BBE\u63CF\u8FF0/\u6027\u683C/\u80CC\u666F...", value: newDefine, onChange: (e) => setNewDefine(e.currentTarget.value), styles: { input: { backgroundColor: '#f5f6f7', color: 'var(--app-text-primary)' } }, onKeyDown: (e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    (async () => {
                                        const ok = await createPersonaRemote(newName, newDefine);
                                        if (ok) {
                                            setAddOpen(false);
                                            setNewName('');
                                            setNewDefine('');
                                        }
                                    })();
                                    e.preventDefault();
                                }
                            } }), _jsxs(Group, { justify: "center", gap: "md", children: [_jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#9AA0A6' }, label: { color: '#fff' } }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = '#7f8690'), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = '#9AA0A6'), onClick: () => setAddOpen(false), children: "\u53D6\u6D88" }), _jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#87CEFA' }, label: { color: '#fff' } }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = '#61b7f0'), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = '#87CEFA'), onClick: () => {
                                        (async () => {
                                            const ok = await createPersonaRemote(newName, newDefine);
                                            if (ok) {
                                                setAddOpen(false);
                                                setNewName('');
                                                setNewDefine('');
                                            }
                                        })();
                                    }, disabled: !newName.trim() || !newDefine.trim(), children: "\u521B\u5EFA" })] })] }) }), _jsx(Modal, { opened: summaryModalOpen, onClose: () => setSummaryModalOpen(false), centered: true, title: _jsx("div", { style: { width: '100%', textAlign: 'center', color: 'var(--app-text-primary)' }, children: "\u5BF9\u8BDD\u603B\u7ED3" }), ...modalCommonProps, children: _jsxs(Stack, { gap: "md", children: [_jsx(Textarea, { minRows: 8, autosize: true, placeholder: "AI \u603B\u7ED3\u5185\u5BB9", value: summaryDraft, onChange: (e) => setSummaryDraft(e.currentTarget.value), styles: { input: { backgroundColor: '#f5f6f7', color: 'var(--app-text-primary)' } } }), _jsxs(Group, { justify: "center", gap: "md", children: [_jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#9AA0A6' }, label: { color: '#fff' } }, onClick: () => setSummaryModalOpen(false), children: "\u53D6\u6D88" }), _jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#87CEFA' }, label: { color: '#fff' } }, onClick: () => { setSelfMemory(summaryDraft || ''); setSummaryModalOpen(false); notifications.show({ title: '已保存', message: '已更新角色记忆', color: 'green' }); }, children: "\u4FDD\u5B58" })] })] }) }), _jsx(Modal, { opened: commentModalOpen, onClose: () => setCommentModalOpen(false), centered: true, title: _jsx("div", { style: { width: '100%', textAlign: 'center', color: 'var(--app-text-primary)' }, children: "\u53D1\u8868\u8BC4\u8BBA" }), ...modalCommonProps, children: _jsxs(Stack, { gap: "md", align: "center", children: [_jsx(Textarea, { placeholder: "\u5199\u70B9\u4EC0\u4E48\u2026", minRows: 4, autosize: true, value: commentDraft, onChange: (e) => setCommentDraft(e.currentTarget.value), styles: { input: { backgroundColor: '#f5f6f7', color: 'var(--app-text-primary)' } }, onKeyDown: (e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    (async () => {
                                        const txt = (commentDraft || '').trim();
                                        const id = commentTargetId;
                                        if (!txt || !id)
                                            return;
                                        const meName = (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || '我';
                                        const cmt = { id: `c_${Date.now()}`, userName: meName, avatar: avatar || undefined, text: txt, ts: Date.now() };
                                        setFeeds((prev) => (prev || []).map((x) => x.id === id ? { ...x, comments: [...x.comments, cmt] } : x));
                                        try {
                                            const tk = getStoredAuthToken();
                                            if (tk) {
                                                const base = getApiBase();
                                                await httpFetch(`${base}/api/feeds/comment`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ id, text: txt }) });
                                            }
                                            else {
                                                setAuthModalOpen(true);
                                            }
                                        }
                                        catch { }
                                        setCommentDraft('');
                                        setCommentModalOpen(false);
                                        setCommentTargetId(null);
                                    })();
                                    e.preventDefault();
                                }
                            } }), _jsxs(Group, { justify: "center", gap: "md", children: [_jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#9AA0A6' }, label: { color: '#fff' } }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = '#7f8690'), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = '#9AA0A6'), onClick: () => { setCommentModalOpen(false); setCommentDraft(''); setCommentTargetId(null); }, children: "\u53D6\u6D88" }), _jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#87CEFA' }, label: { color: '#fff' } }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = '#61b7f0'), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = '#87CEFA'), onClick: async () => {
                                        const txt = (commentDraft || '').trim();
                                        const id = commentTargetId;
                                        if (!txt || !id)
                                            return;
                                        const meName = (selfNickname && String(selfNickname).trim()) || (nickname && nickname.trim()) || '我';
                                        const cmt = { id: `c_${Date.now()}`, userName: meName, avatar: avatar || undefined, text: txt, ts: Date.now() };
                                        setFeeds((prev) => (prev || []).map((x) => x.id === id ? { ...x, comments: [...x.comments, cmt] } : x));
                                        try {
                                            const tk = getStoredAuthToken();
                                            if (tk) {
                                                const base = getApiBase();
                                                await httpFetch(`${base}/api/feeds/comment`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ id, text: txt }) });
                                            }
                                            else {
                                                setAuthModalOpen(true);
                                            }
                                        }
                                        catch { }
                                        setCommentDraft('');
                                        setCommentModalOpen(false);
                                        setCommentTargetId(null);
                                    }, children: "\u53D1\u9001" })] })] }) }), _jsx(Modal, { opened: nickModalOpen, onClose: () => setNickModalOpen(false), centered: true, title: _jsx("div", { style: { width: '100%', textAlign: 'center', color: 'var(--app-text-primary)' }, children: "\u4FEE\u6539\u6635\u79F0" }), ...modalCommonProps, children: _jsxs(Stack, { gap: "md", align: "center", children: [_jsx(TextInput, { value: nickDraft, onChange: (e) => setNickDraft(e.currentTarget.value), placeholder: "\u8F93\u5165\u65B0\u7684\u6635\u79F0", styles: { input: { textAlign: 'center', backgroundColor: '#f5f6f7', color: 'var(--app-text-primary)' } }, onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    const v = (nickDraft || '').trim();
                                    if (v && current) {
                                        setPersonas((list) => list.map((p) => p.id === current.id ? { ...p, name: v } : p));
                                        setNickModalOpen(false);
                                    }
                                }
                            } }), _jsxs(Group, { justify: "center", gap: "md", children: [_jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#9AA0A6' }, label: { color: '#fff' } }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = '#7f8690'), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = '#9AA0A6'), onClick: () => setNickModalOpen(false), children: "\u53D6\u6D88" }), _jsx(Button, { variant: "filled", radius: "md", styles: { root: { backgroundColor: '#87CEFA' }, label: { color: '#fff' } }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = '#61b7f0'), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = '#87CEFA'), onClick: () => {
                                        const v = (nickDraft || '').trim();
                                        if (v && current) {
                                            setPersonas((list) => list.map((p) => p.id === current.id ? { ...p, name: v } : p));
                                            setNickModalOpen(false);
                                        }
                                    }, children: "\u4FDD\u5B58" })] })] }) })] }));
};
export default WhatsApp;
